import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db8clnrk13ra8mig8pig';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormMessage('感谢你的反馈，我们会尽快处理。');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const body = new URLSearchParams();
    formData.forEach((val, key) => {
      body.append(key, typeof val === 'string' ? val : val.name);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (!isOk || isSpam) {
        setStatus('error');
        setFormError(
          typeof serverMessage === 'string' && serverMessage.trim()
            ? serverMessage
            : '提交失败，请稍后重试，或直接发送邮件联系我们。',
        );
        return;
      }

      setStatus('success');
      setFormMessage(parsed?.meta?.message || '感谢你的反馈，我们会尽快处理。');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试，或直接发送邮件联系我们。');
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱地址
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收我们的回复"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="fb-subject"
          name="subject"
          defaultValue="内容纠错"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none"
        >
          <option value="内容纠错">内容纠错</option>
          <option value="内容建议">内容建议</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          placeholder="请描述你遇到的问题或建议，最多 500 字"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
      </div>

      <div className="form-honeypot-field" aria-hidden="true">
        <label htmlFor="fb-website-alt">备用联系站点</label>
        <input
          id="fb-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === 'error' && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base" />
          {formError}
        </p>
      )}
      {status === 'success' && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5 text-base" />
          {formMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
        {status === 'submitting' ? '提交中…' : '提交反馈'}
      </button>
    </form>
  );
}